/**
 * The chat picker. `/message/send` with `kind: "gameCard"` is this client's
 * whole contract: the server builds the frozen snapshot inside its own
 * handler by calling `/event/share-card-get` itself, so this file never
 * calls it and never assembles a card payload from live event data.
 */
import {
  useChatListMine,
  useMessageSend,
} from "@sportsmet/messaging-sdk/private";
import { Button, Dialog, FOCUS_RING, Skeleton } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";
import { resolveViewerTimeZone } from "../scores/viewerTimeZone";
import {
  buildShareRequestId,
  buildShareSentBody,
  selectShareableChats,
  type typeShareableChat,
} from "./shareChatLogic";

export const ShareToChatDialog = ({
  eventId,
  open,
  onClose,
  onOpenChat,
  onStartChat,
}: {
  eventId: string;
  open: boolean;
  onClose: () => void;
  /** Called with the chat the game was just sent to. The route navigates; the dialog does not. */
  onOpenChat: (chatId: string) => void;
  /** Called from the no-chats state. The route navigates to the new chat dialog. */
  onStartChat: () => void;
}): ReactNode => {
  const chats = useChatListMine({});
  const send = useMessageSend();
  const [sentTo, setSentTo] = useState<typeShareableChat | null>(null);

  const rows = selectShareableChats(chats.data?.data?.items ?? []);

  const close = (): void => {
    setSentTo(null);
    onClose();
  };
  const isEmpty =
    sentTo === null &&
    !chats.isPending &&
    chats.data?.error == null &&
    rows.length === 0;

  const shareTo = (row: typeShareableChat): void => {
    send.mutate(
      {
        chatId: row.chatId,
        requestId: buildShareRequestId(),
        kind: "gameCard",
        gameCardEventId: eventId,
        timeZone: resolveViewerTimeZone(),
      },
      {
        onSuccess: (result) => {
          if (showApiError(result)) return;
          setSentTo(row);
        },
        onError: () => {
          showOfflineError();
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      title="Share to a chat"
      description={
        sentTo === null
          ? "Pick a chat. We send the game as it looks right now."
          : undefined
      }
      autoFocus={false}
      footer={
        sentTo !== null ? (
          <>
            <Button variant="secondary" onClick={close}>
              Done
            </Button>
            <Button
              onClick={() => {
                onOpenChat(sentTo.chatId);
              }}
            >
              Open chat
            </Button>
          </>
        ) : isEmpty ? (
          <>
            <Button variant="secondary" onClick={close}>
              Done
            </Button>
            <Button onClick={onStartChat}>Start a chat</Button>
          </>
        ) : (
          <Button variant="secondary" fullWidth onClick={close}>
            Cancel
          </Button>
        )
      }
    >
      {sentTo !== null ? (
        <div
          role="status"
          className="flex flex-col items-center gap-2 py-6 text-center"
        >
          <p className="text-body-strong text-ink">Sent</p>
          <p className="text-body text-ink-secondary">
            {buildShareSentBody(sentTo.title)}
          </p>
        </div>
      ) : chats.isPending ? (
        <div className="flex flex-col gap-3">
          <Skeleton shape="control" label="Loading your chats" />
          <Skeleton shape="control" />
          <Skeleton shape="control" />
        </div>
      ) : chats.data?.error != null ? (
        <p className="text-body text-ink-secondary">
          We could not load your chats. Close this and try again.
        </p>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <p className="text-body-strong text-ink">No chats yet</p>
          <p className="text-body text-ink-secondary">
            Join a pool or start a chat, then you can share a game here.
          </p>
        </div>
      ) : (
        <div className="flex max-h-100 flex-col gap-1 overflow-y-auto">
          {rows.map((row) => (
            <button
              key={row.chatId}
              type="button"
              disabled={send.isPending && send.variables.chatId === row.chatId}
              onClick={() => {
                shareTo(row);
              }}
              className={`flex min-h-touch-target w-full flex-col items-start gap-1 rounded-md border border-hairline bg-surface p-3 text-left hover:bg-surface-sunken disabled:cursor-not-allowed disabled:bg-disabled-bg ${FOCUS_RING}`}
            >
              <span className="text-body text-ink">{row.title}</span>
              {row.lastMessagePreview !== null ? (
                <span className="text-caption text-ink-secondary">
                  {row.lastMessagePreview}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      )}
    </Dialog>
  );
};
